import type { SuretyAccountId } from "@inrev/common";
import { type ReactNode, useMemo } from "react";
import { SideDrawerTrigger, useSideDrawer } from "../patterns/side-drawer";
import {
	FinancialsPeriodSelectorDrawer,
	type PeriodSelectorTableAction,
} from "./FinancialsPeriodSelectorDrawer";
import { useFinancialsPeriodSelector } from "./FinancialsPeriodSelectorProvider";

const EMPTY_EXPANDABLE_ROW_IDS: string[] = [];

export const PERIOD_SELECTOR_TEST_ID = "period-selector";

/** The "Show P/P % change" toggle shared by every period-selector overlay. */
export const ppChangeTableAction = (
	checked: boolean,
	onToggle: () => void,
): PeriodSelectorTableAction => ({
	key: "ppChange",
	label: "Show P/P % change",
	checked,
	onToggle,
	testId: "show-pp-change-toggle",
});

/** The "Hide empty line items" toggle used by the financial statement grids. */
export const hideEmptyRowsTableAction = (
	checked: boolean,
	onToggle: () => void,
): PeriodSelectorTableAction => ({
	key: "hideEmptyRows",
	label: "Hide empty line items",
	checked,
	onToggle,
	testId: "hide-empty-rows-toggle",
});

type UsePeriodSelectorOverlayArgs = {
	accountId: SuretyAccountId;
	tableActions?: readonly PeriodSelectorTableAction[];
	expandableRowIds?: string[];
	hideExpandControls?: boolean;
	showFullLabel?: boolean;
	/** Extra controls rendered inline beside the trigger (e.g. the view-mode toggle). */
	headerActions?: ReactNode;
};

type PeriodSelectorOverlay = {
	/** Drop into `DataGrid`'s `labelColumn.renderHeader`. */
	periodSelectorHeader: ReactNode;
	/** Drop into `DataGrid`'s `overlay`. */
	periodSelectorOverlay: ReactNode;
};

/**
 * Owns the period-selector drawer wiring every {@link DataGrid}-backed financial grid shares: the
 * open/close state, the trigger header, and the drawer overlay. Consumers supply only what differs
 * (their table-action toggles and any inline header controls) and drop the returned `header`/`overlay`
 * straight into the grid.
 */
export const usePeriodSelectorOverlay = ({
	accountId,
	tableActions,
	expandableRowIds = EMPTY_EXPANDABLE_ROW_IDS,
	hideExpandControls,
	showFullLabel,
	headerActions,
}: UsePeriodSelectorOverlayArgs): PeriodSelectorOverlay => {
	const { triggerProps, drawerProps } = useSideDrawer({ testId: PERIOD_SELECTOR_TEST_ID });
	const { selectedCountForTab } = useFinancialsPeriodSelector();

	const periodSelectorHeader = useMemo(
		() => (
			<div className="flex items-center gap-2">
				<SideDrawerTrigger
					{...triggerProps}
					count={selectedCountForTab}
					label="Periods"
					iconType="calendar"
					showLabel={showFullLabel}
				/>
				{headerActions}
			</div>
		),
		[triggerProps, selectedCountForTab, showFullLabel, headerActions],
	);

	const periodSelectorOverlay = useMemo(
		() => (
			<FinancialsPeriodSelectorDrawer
				accountId={accountId}
				{...drawerProps}
				expandableRowIds={expandableRowIds}
				tableActions={tableActions}
				hideExpandControls={hideExpandControls}
			/>
		),
		[accountId, drawerProps, expandableRowIds, tableActions, hideExpandControls],
	);

	return { periodSelectorHeader, periodSelectorOverlay };
};
